import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Button from '@/common/components/ui/Button';
import Card from '@/components/Card';

import { ChevronDownIcon, ChevronUpIcon } from '@/assets/icons/chevron';

import { QuizAttemptData, QuizQuestionData, SavedFR, SavedMC } from 'quiz-types';
import { CheckmarkCircleIcon } from '@/assets/ui';

const propTypes = {
  questionIndex: PropTypes.number.isRequired,
  questionSet: PropTypes.array.isRequired,
  submittedAnswers: PropTypes.any,
  swapQuestion: PropTypes.func.isRequired,
  isSubmitDisabled: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};

interface Props {
  questionIndex: number;
  questionSet: Array<QuizQuestionData>;
  submittedAnswers?: Record<number, number | string>;
  swapQuestion: (index: number) => void;
  isSubmitDisabled: () => boolean;
  handleSubmit: () => void;
  quizAttempt?: QuizAttemptData;
  onBack?: () => void;
}

function QuizQuestionsList({
  questionIndex,
  questionSet,
  submittedAnswers,
  swapQuestion,
  isSubmitDisabled,
  handleSubmit,
  quizAttempt,
  onBack,
}: Props) {
  const [expanded, setExpanded] = useState<boolean>(true);

  // Checks if the question is complete
  const isQuestionComplete = (questionIndex: number): boolean => {
    const currentQuestion = questionSet[questionIndex];
    // If the question is not answered, return false
    if (submittedAnswers === undefined) {
      return false;
    }
    // Check if the question is answered
    switch (currentQuestion.type) {
      case 'Multiple Choice':
        if (submittedAnswers[questionIndex] == 0 || !!submittedAnswers[questionIndex]) {
          return true;
        }
        return false;
      case 'Paragraph':
        if (submittedAnswers[questionIndex]) {
          return true;
        }
        break;
      case 'Short Answer':
        if (submittedAnswers[questionIndex]) {
          return true;
        }
        break;
      default:
        break;
    }
    return false;
  };

  const getQuestionColor = (questionIndex: number): string => {
    const currentQuestion = questionSet[questionIndex];
    let userAnswer: SavedMC | SavedFR | undefined;
    if (currentQuestion.type === 'Multiple Choice') {
      userAnswer = (quizAttempt?.mc_data ?? {})[questionIndex];
    } else {
      userAnswer = (quizAttempt?.fr_data ?? {})[questionIndex];
    }
    const isUngraded = !userAnswer?.gradedCorrect && userAnswer?.gradedCorrect !== false;
    const isCorrect = userAnswer?.gradedCorrect || ((userAnswer as SavedMC)?.correct && isUngraded);

    if (isCorrect) return 'bg-success_highlight';
    return 'bg-failure';
  };

  return (
    <Card
      className={`hidden md:flex flex-col h-fit ml-8 bg-surface_gray_50 border border-border_gray_200 drop-shadow-md rounded-xl p-4 w-24 lg:w-80`}
    >
      <button
        className='flex items-center justify-center gap-2 font-semibold text-sm'
        onClick={() => setExpanded(!expanded)}
      >
        <span className='hidden lg:flex'>Questions</span>
        {expanded ? <ChevronUpIcon className='w-4' /> : <ChevronDownIcon className='w-4' />}
      </button>
      <div className={`flex flex-col gap-3 mt-4 ${expanded ? '' : 'hidden'}`}>
        {questionSet.map((question, i) => (
          <Button
            key={i}
            onClick={() => swapQuestion(i)}
            outline={true}
            className={`w-full justify-between items-center rounded-md border-2 px-3 py-2 text-sm
              ${!quizAttempt ? 'bg-success_highlight' : getQuestionColor(i)}
              ${questionIndex === i ? 'border-primary text-primary' : 'border-border_gray_200 dark:border-gray-500 hover:border-primary hover:text-primary'}
            `}
          >
            <span
              className={`hidden lg:flex w-full text-left font-semibold ${questionIndex === i ? '' : ''}`}
            >
              Question {i + 1}
            </span>
            <span className={`flex lg:hidden self-center font-semibold`}>Q{i + 1}</span>
            <CheckmarkCircleIcon
              className={`hidden lg:flex w-6 bg-success rounded-full p-1 ${isQuestionComplete(i) ? '' : 'hidden lg:hidden'}`}
            />
          </Button>
        ))}

        <Button
          className='w-full lg:flex hidden mt-2'
          onClick={onBack ? onBack : handleSubmit}
          disabled={
            onBack
              ? false
              : quizAttempt
                ? (!quizAttempt.grade && quizAttempt.grade !== 0) ||
                  quizAttempt.grade > 100 ||
                  quizAttempt.grade < 0
                : isSubmitDisabled()
          }
        >
          {onBack ? 'Return to Student Course View' : quizAttempt ? 'Submit Grade' : 'Submit Quiz'}
        </Button>
      </div>
    </Card>
  );
}

(QuizQuestionsList as any).propTypes = propTypes;
export default QuizQuestionsList;
